import { ref, computed, effectScope, watch } from 'vue'
import { failureMessage } from '@/apps/core/utils'
import { defineStore } from 'pinia'
import * as tus from 'tus-js-client'
import { deleteAsset } from '@/api/assets/assets'
import { isRetryable } from '@/api/client'
import { useCreditsExhaustedStore } from '@/apps/billing/stores/creditsExhausted'
import {
  openAssetUpload,
  uploadAssetDirect,
  UPLOAD_TOKEN_HEADER,
  type AssetUploadOpened,
  type OpenAssetUploadRequest,
} from '@/api/assets/uploads'
import type { UploadItem, UploadStoreConfig, UploadProgress, UploadMetadata, UploadResult } from '../types/upload.types'
import { RESTART_KINDS, uploadFailureKind } from '../utils/uploadFailure'

/**
 * Below this size a file goes up in one request; from it on, through a resumable session.
 *
 * Small because the one-shot route is cut at thirty seconds like every request of the API, and
 * that bound counts the time the browser takes to send the body: 1 MiB lands down to about
 * 280 kbit/s of upstream, where 5 MiB needed 1.4 Mbit/s. A file above it pays one request more —
 * the session's opening — and a file under the 5 MiB chunk is then one chunk, sent to the upload
 * server, which waits minutes for a slow link.
 */
const DIRECT_UPLOAD_THRESHOLD = 1024 * 1024
const SPEED_SAMPLE_COUNT = 5 // Number of samples for moving average

export const MAX_QUEUE_SIZE = 50

const DEFAULT_CONFIG: UploadStoreConfig = {
  maxConcurrent: 3,
  // The object store's floor for every part but the last, and well under the 50 MB a chunk may be.
  chunkSize: 5 * 1024 * 1024,
  // tus-js-client counts its attempts again from zero after every chunk that lands, so the longer
  // tail only matters while chunks keep failing — a dropped link, the upload server restarting —
  // and gives either a minute to come back before the upload is reported failed.
  retryDelays: [0, 1000, 3000, 5000, 10000, 20000, 30000],
  maxRetries: 3,
}

export type UploadCompleteCallback = (upload: UploadItem) => void

function getAssetNameFromFilename(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex > 0) {
    return filename.substring(0, lastDotIndex)
  }
  return filename
}

/**
 * What a queued file asks the server for. Each mode takes its own fields and the server refuses
 * the others by name: `create` names a shelf and a folder, `version` and `related` name the asset
 * they are against and act in its workspace. `totalSize` is a session's; the one-shot route reads
 * the size off the bytes.
 */
function uploadRequest(metadata: UploadMetadata, totalSize?: number): OpenAssetUploadRequest {
  const common = {
    filename: metadata.filename,
    content_type: metadata.contentType,
    total_size: totalSize,
  }
  switch (metadata.uploadMode ?? 'create') {
    case 'version':
      return { ...common, mode: 'version', target_asset_id: metadata.targetAssetId }
    case 'related':
      return {
        ...common,
        mode: 'related',
        target_asset_id: metadata.targetAssetId,
        relationship_kind: metadata.relationshipKind,
        is_public: metadata.isPublic ?? false,
        name: getAssetNameFromFilename(metadata.filename),
      }
    case 'create':
      return {
        ...common,
        mode: 'create',
        workspace_id: metadata.workspaceId,
        parent_id: metadata.parentFolderId ?? null,
        name: getAssetNameFromFilename(metadata.filename),
      }
  }
}

/** The headers every chunk request of a session carries: its token is its whole credential. */
function chunkHeaders(opened: Pick<AssetUploadOpened, 'upload_token'>): Record<string, string> {
  return { [UPLOAD_TOKEN_HEADER]: opened.upload_token }
}

/**
 * Put in the trash an asset a session created and nobody will finish. Terminating a session does
 * not settle the asset it opened, and neither does its expiry: left alone it stays `uploading`,
 * listed nowhere, for good. A `version` upload created nothing and is never discarded.
 */
function discardCreatedAsset(metadata: UploadMetadata, assetId: string | undefined): void {
  if (!assetId || metadata.uploadMode === 'version') return
  deleteAsset(assetId).catch((error: unknown) => {
    console.warn('An abandoned upload left an asset that could not be put in the trash:', error)
  })
}

export const useUploadStore = defineStore('assets-upload', () => {
  const uploads = ref<Map<string, UploadItem>>(new Map())
  const config = ref<UploadStoreConfig>({ ...DEFAULT_CONFIG })
  const drawerOpen = ref(false)
  const drawerTab = ref<'uploads' | 'exports'>('uploads')
  const dropOverlayDisabled = ref(false)
  /**
   * Lets a screen that reports upload progress itself keep the drawer shut.
   *
   * Only auto-opening is suppressed — opening it by hand still works. The
   * onboarding scene is the case it was added for: it draws its own progress,
   * and the drawer popped over the steps column to say the same thing twice.
   */
  const drawerAutoOpenDisabled = ref(false)
  const onUploadCompleteCallbacks = ref<UploadCompleteCallback[]>([])

  // Speed tracking
  const speedSamples = ref<number[]>([])
  const lastBytesUploaded = ref(0)
  const lastSpeedCheckTime = ref(Date.now())
  const uploadSpeed = ref(0)
  let speedInterval: ReturnType<typeof setInterval> | null = null

  const uploadList = computed(() => Array.from(uploads.value.values()))

  const pendingUploads = computed(() => uploadList.value.filter((u) => u.status === 'pending'))

  const activeUploads = computed(() => uploadList.value.filter((u) => u.status === 'uploading'))

  const pausedUploads = computed(() => uploadList.value.filter((u) => u.status === 'paused'))

  const completedUploads = computed(() => uploadList.value.filter((u) => u.status === 'completed'))

  const failedUploads = computed(() => uploadList.value.filter((u) => u.status === 'failed'))

  const hasActiveOrPendingUploads = computed(() => activeUploads.value.length > 0 || pendingUploads.value.length > 0)

  // Items occupying a slot in the queue: anything not yet finished or failed.
  const queueSize = computed(
    () => activeUploads.value.length + pendingUploads.value.length + pausedUploads.value.length,
  )

  const availableSlots = computed(() => Math.max(0, MAX_QUEUE_SIZE - queueSize.value))

  // ETA calculation based on current speed and remaining bytes
  const uploadEta = computed(() => {
    if (uploadSpeed.value === 0) return null

    let remainingBytes = 0
    for (const upload of [...activeUploads.value, ...pendingUploads.value]) {
      remainingBytes += upload.bytesTotal - upload.bytesUploaded
    }

    if (remainingBytes <= 0) return null
    return remainingBytes / uploadSpeed.value
  })

  // Calculate upload speed using moving average
  function calculateSpeed() {
    const now = Date.now()
    const timeDelta = (now - lastSpeedCheckTime.value) / 1000 // seconds

    if (timeDelta < 0.5) return // Don't calculate too frequently

    let currentTotalUploaded = 0
    for (const upload of activeUploads.value) {
      currentTotalUploaded += upload.bytesUploaded
    }

    const bytesDelta = currentTotalUploaded - lastBytesUploaded.value
    const currentSpeed = bytesDelta / timeDelta

    // Only add positive speed samples
    if (currentSpeed > 0) {
      speedSamples.value.push(currentSpeed)
      if (speedSamples.value.length > SPEED_SAMPLE_COUNT) {
        speedSamples.value.shift()
      }
    }

    // Calculate moving average
    if (speedSamples.value.length > 0) {
      const sum = speedSamples.value.reduce((a, b) => a + b, 0)
      uploadSpeed.value = sum / speedSamples.value.length
    } else {
      uploadSpeed.value = 0
    }

    lastBytesUploaded.value = currentTotalUploaded
    lastSpeedCheckTime.value = now
  }

  function startSpeedTracking() {
    if (speedInterval) return

    // Reset tracking state
    lastBytesUploaded.value = 0
    lastSpeedCheckTime.value = Date.now()
    speedSamples.value = []
    uploadSpeed.value = 0

    speedInterval = setInterval(calculateSpeed, 500)
  }

  function stopSpeedTracking() {
    if (speedInterval) {
      clearInterval(speedInterval)
      speedInterval = null
    }
    uploadSpeed.value = 0
    speedSamples.value = []
  }

  // Watch for active uploads to start/stop speed tracking
  watch(
    () => activeUploads.value.length,
    (newCount, oldCount = 0) => {
      if (newCount > 0 && oldCount === 0) {
        startSpeedTracking()
      } else if (newCount === 0 && oldCount > 0) {
        stopSpeedTracking()
      }
    },
    { immediate: true },
  )

  const overallProgress = computed<UploadProgress>(() => {
    const active = activeUploads.value
    const pending = pendingUploads.value
    const completed = completedUploads.value
    const failed = failedUploads.value

    let totalBytes = 0
    let uploadedBytes = 0

    for (const upload of [...active, ...pending]) {
      totalBytes += upload.bytesTotal
      uploadedBytes += upload.bytesUploaded
    }

    return {
      activeCount: active.length,
      pendingCount: pending.length,
      completedCount: completed.length,
      failedCount: failed.length,
      totalProgress: totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0,
    }
  })

  function generateId(): string {
    return `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  function updateUpload(id: string, updates: Partial<UploadItem>): void {
    const item = uploads.value.get(id)
    if (item) {
      uploads.value.set(id, { ...item, ...updates })
    }
  }

  function notifyUploadComplete(upload: UploadItem): void {
    for (const callback of onUploadCompleteCallbacks.value) {
      try {
        callback(upload)
      } catch (error) {
        console.error('Error in upload complete callback:', error)
      }
    }
  }

  function onUploadComplete(callback: UploadCompleteCallback): () => void {
    onUploadCompleteCallbacks.value.push(callback)
    return () => {
      const index = onUploadCompleteCallbacks.value.indexOf(callback)
      if (index > -1) {
        onUploadCompleteCallbacks.value.splice(index, 1)
      }
    }
  }

  function processQueue(): void {
    const currentActive = activeUploads.value.length
    const slotsAvailable = config.value.maxConcurrent - currentActive

    if (slotsAvailable <= 0) return

    const pending = pendingUploads.value.slice(0, slotsAvailable)
    for (const upload of pending) {
      startUpload(upload.id)
    }
  }

  /** The bytes are all in: the asset exists and carries its file, and the pipeline takes it on. */
  function completeUpload(id: string, assetUid: string): void {
    const item = uploads.value.get(id)
    if (!item) return
    const result: UploadResult = {
      assetUid,
      filename: item.metadata.filename,
      contentType: item.metadata.contentType,
      size: item.bytesTotal,
    }
    updateUpload(id, {
      status: 'completed',
      progress: 100,
      bytesUploaded: item.bytesTotal,
      completedAt: Date.now(),
      result,
    })
    const completedUpload = uploads.value.get(id)
    if (completedUpload) {
      notifyUploadComplete(completedUpload)
    }
  }

  function failUpload(id: string, error: unknown, fallback: string): void {
    const failureKind = uploadFailureKind(error)
    updateUpload(id, {
      status: 'failed',
      failureKind,
      error: failureMessage(error, fallback),
    })
    if (failureKind === 'creditsExhausted') reportCreditRefusal()
  }

  /**
   * Tell the « insufficient credits » modal that the credit refused an upload, and that a top-up
   * may send every upload it refused again (`stores/creditsExhausted.ts`). The opening's refusal
   * reaches the modal through the client's own handler as well; the last chunk's comes from the
   * upload server, which no handler hears, so this report is the only one that refusal gets.
   *
   * **No cost is said**: the opening refuses a balance at or below zero whatever the file, and a
   * video's month is only known once it is measured, so the modal says the balance does not cover
   * the upload rather than a figure the server did not use.
   */
  function reportCreditRefusal(): void {
    void useCreditsExhaustedStore().report({ retry: async () => retryCreditRefused() })
  }

  /** Send again every upload the credit refused — once a top-up has landed. */
  function retryCreditRefused(): void {
    for (const item of uploadList.value) {
      if (item.status === 'failed' && item.failureKind === 'creditsExhausted') retryUpload(item.id)
    }
  }

  /**
   * Stop an upload for good. A direct upload's request is aborted. A session is terminated, so
   * the object store stops holding its parts, and the asset it created goes to the trash.
   */
  function abandon(item: UploadItem): void {
    if (item.uploadMethod === 'direct') {
      item.abortController?.abort()
      return
    }
    if (!item.tusUpload) return
    item.tusUpload.abort(true).catch((error: unknown) => {
      console.warn('An abandoned upload session could not be terminated:', error)
    })
    discardCreatedAsset(item.metadata, item.assetUid)
  }

  /** Whether the server holds something of this upload that a stop has to give back. */
  function isUnfinished(item: UploadItem): boolean {
    return item.status === 'uploading' || item.status === 'paused' || item.status === 'failed'
  }

  function createTusUpload(item: UploadItem, opened: AssetUploadOpened): tus.Upload {
    // The session exists already: the client resumes it at `location` — a `HEAD` for the
    // offset, then the chunks — and never creates one, having no `endpoint` to create it at.
    const tusUpload = new tus.Upload(item.file, {
      uploadUrl: opened.location,
      headers: chunkHeaders(opened),
      retryDelays: config.value.retryDelays,
      chunkSize: config.value.chunkSize,
      // **Nothing is remembered, and the reason is the file rather than the token.** A tus
      // fingerprint points at a session this page could no longer send to — the token is handed
      // over once — but that half is fixable: the session cookie identifies the caller, the row
      // carries `created_by_id`, and a route could mint a fresh token for a session the caller
      // owns. What is not fixable here is that **a reload loses the `File`**: the browser hands
      // no way back to the bytes somebody picked, and an upload cannot be resumed without them.
      //
      // So resuming across a reload is not a flag, it is a screen — list the unfinished
      // sessions, ask for the file again, check it is the same one. Deferred with its options
      // and their costs in `docs/plans/storage-core.md`, Out of scope.
      storeFingerprintForResuming: false,
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        updateUpload(item.id, {
          bytesUploaded,
          bytesTotal,
          progress: Math.round((bytesUploaded / bytesTotal) * 100),
        })
      },
      onSuccess: () => {
        completeUpload(item.id, opened.asset_id)
        processQueue()
      },
      // Called once the client's own retries (`retryDelays`) are spent. The session is kept:
      // retrying resumes it from the offset the server answers.
      onError: (error: Error) => {
        failUpload(item.id, error, 'Upload failed')
        processQueue()
      },
    })

    return tusUpload
  }

  function addFiles(files: File[], metadata: Partial<UploadMetadata>): string[] {
    if (!metadata.workspaceId) {
      console.error('workspaceId is required for asset uploads')
      return []
    }

    const ids: string[] = []

    for (const file of files) {
      const id = generateId()

      const uploadItem: UploadItem = {
        id,
        file,
        status: 'pending',
        progress: 0,
        bytesUploaded: 0,
        bytesTotal: file.size,
        error: null,
        uploadUrl: null,
        tusUpload: null,
        metadata: {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          workspaceId: metadata.workspaceId,
          parentFolderId: metadata.parentFolderId,
          uploadMode: metadata.uploadMode,
          targetAssetId: metadata.targetAssetId,
          relationshipKind: metadata.relationshipKind,
          isPublic: metadata.isPublic,
        },
        createdAt: Date.now(),
        completedAt: null,
        retryCount: 0,
      }

      uploads.value.set(id, uploadItem)
      ids.push(id)
    }

    openDrawerAutomatically()

    processQueue()
    return ids
  }

  function startUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item || item.status !== 'pending') return

    if (item.file.size < DIRECT_UPLOAD_THRESHOLD) {
      void startDirectUpload(id)
    } else {
      void startTusUpload(id)
    }
  }

  async function startTusUpload(id: string): Promise<void> {
    const item = uploads.value.get(id)
    if (!item) return

    // Out of `pending` before the session is asked for, so the queue does not start it twice.
    updateUpload(id, { status: 'uploading', uploadMethod: 'tus' })

    let opened: AssetUploadOpened
    try {
      // The asset exists from this answer on, before any of its bytes.
      opened = await openAssetUpload(
        uploadRequest(item.metadata, item.file.size),
      )
    } catch (error) {
      const upload = uploads.value.get(id)
      if (!upload || upload.status !== 'uploading') return
      if (isRetryable(error) && upload.retryCount < config.value.maxRetries) {
        updateUpload(id, { retryCount: upload.retryCount + 1 })
        setTimeout(() => startTusUpload(id), config.value.retryDelays[upload.retryCount] ?? 1000)
      } else {
        failUpload(id, error, 'The upload session could not be opened')
        processQueue()
      }
      return
    }

    // Cancelled or removed while the session was being opened: give it back at once.
    const current = uploads.value.get(id)
    if (!current || current.status !== 'uploading') {
      tus.Upload.terminate(opened.location, { headers: chunkHeaders(opened) }).catch((error: unknown) => {
        console.warn('An abandoned upload session could not be terminated:', error)
      })
      discardCreatedAsset(item.metadata, opened.asset_id)
      return
    }

    const tusUpload = createTusUpload(current, opened)
    updateUpload(id, { uploadUrl: opened.location, assetUid: opened.asset_id, tusUpload })
    tusUpload.start()
  }

  async function startDirectUpload(id: string): Promise<void> {
    const item = uploads.value.get(id)
    if (!item) return

    const abortController = new AbortController()

    // One request and no progress events: the bar moves when the answer comes back.
    updateUpload(id, {
      status: 'uploading',
      uploadMethod: 'direct',
      abortController,
    })

    try {
      const uploaded = await uploadAssetDirect(
        uploadRequest(item.metadata),
        item.file,
        abortController.signal,
      )
      completeUpload(id, uploaded.asset_id)
    } catch (error) {
      // Aborted by a cancel or a removal, which already said what the item is now.
      if (abortController.signal.aborted) {
        processQueue()
        return
      }
      const upload = uploads.value.get(id)
      if (upload && isRetryable(error) && upload.retryCount < config.value.maxRetries) {
        updateUpload(id, { retryCount: upload.retryCount + 1 })
        setTimeout(() => startDirectUpload(id), config.value.retryDelays[upload.retryCount] ?? 1000)
        return
      }
      failUpload(id, error, 'Upload failed')
    }

    processQueue()
  }

  function pauseUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item || item.status !== 'uploading' || item.uploadMethod === 'direct') return
    if (!item.tusUpload) return

    void item.tusUpload.abort()
    updateUpload(id, { status: 'paused' })
    processQueue()
  }

  function resumeUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item || item.status !== 'paused' || item.uploadMethod === 'direct') return
    if (!item.tusUpload) return

    updateUpload(id, { status: 'uploading' })
    item.tusUpload.start()
  }

  function cancelUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item) return

    if (isUnfinished(item)) abandon(item)

    updateUpload(id, { status: 'cancelled' })
    processQueue()
  }

  function retryUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item || item.status !== 'failed') return

    // A session that was opened is resumed where the server says it stands, rather than
    // replaced by a second session and a second asset.
    if (item.tusUpload && !(item.failureKind && RESTART_KINDS.has(item.failureKind))) {
      updateUpload(id, { status: 'uploading', error: null, failureKind: undefined })
      item.tusUpload.start()
      return
    }

    // A session the server no longer has — gone, or ended by a completion the credit refused — is
    // not resumed, since every request to it would be refused the same way: the file goes into a
    // new one from its first byte, and the asset the old one created goes to the trash, as an
    // abandoned upload's does.
    if (item.tusUpload) discardCreatedAsset(item.metadata, item.assetUid)

    updateUpload(id, {
      status: 'pending',
      error: null,
      failureKind: undefined,
      retryCount: 0,
      abortController: undefined,
      tusUpload: null,
      uploadUrl: null,
      assetUid: undefined,
      progress: 0,
      bytesUploaded: 0,
    })

    startUpload(id)
  }

  function removeUpload(id: string): void {
    const item = uploads.value.get(id)
    if (!item) return

    if (isUnfinished(item)) abandon(item)

    uploads.value.delete(id)
    processQueue()
  }

  function clearCompleted(): void {
    for (const upload of completedUploads.value) {
      uploads.value.delete(upload.id)
    }
  }

  function clearFailed(): void {
    for (const upload of failedUploads.value) {
      abandon(upload)
      uploads.value.delete(upload.id)
    }
  }

  function clearAll(): void {
    for (const upload of uploadList.value) {
      if (isUnfinished(upload)) abandon(upload)
      uploads.value.delete(upload.id)
    }
  }

  // Remove every item that is not currently in flight: completed, failed,
  // and cancelled. Keeps pending/uploading/paused untouched.
  function clearInactive(): void {
    for (const upload of uploadList.value) {
      if (upload.status === 'completed' || upload.status === 'failed' || upload.status === 'cancelled') {
        if (upload.status === 'failed') abandon(upload)
        uploads.value.delete(upload.id)
      }
    }
  }

  // Cancel everything still in flight (pending/uploading/paused) without
  // wiping completed/failed history — used by the "cancel all in progress"
  // bulk action in the drawer.
  function cancelActive(): void {
    const ids = [...activeUploads.value, ...pendingUploads.value, ...pausedUploads.value].map((u) => u.id)
    for (const id of ids) {
      cancelUpload(id)
    }
  }

  function getUpload(id: string): UploadItem | undefined {
    return uploads.value.get(id)
  }

  /**
   * Wait for the asset_uid to become available for a given upload.
   * This resolves as soon as the session is opened (before its bytes land).
   * For direct uploads, it resolves when the upload completes.
   *
   * @param id Upload ID
   * @param timeoutMs Timeout in milliseconds (default: 30000)
   * @returns Promise that resolves with the asset_uid or rejects on timeout/failure
   */
  function waitForAssetUid(id: string, timeoutMs: number = 30000): Promise<string> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()

      const checkAssetUid = () => {
        const upload = uploads.value.get(id)

        if (!upload) {
          reject(new Error('Upload not found'))
          return
        }

        if (upload.status === 'failed' || upload.status === 'cancelled') {
          reject(new Error(upload.error || 'Upload failed'))
          return
        }

        // Check if asset_uid is available
        if (upload.assetUid) {
          resolve(upload.assetUid)
          return
        }

        // For completed uploads, check result
        if (upload.status === 'completed' && upload.result?.assetUid) {
          resolve(upload.result.assetUid)
          return
        }

        // Check timeout
        if (Date.now() - startTime > timeoutMs) {
          reject(new Error('Timeout waiting for asset_uid'))
          return
        }

        // Keep polling
        setTimeout(checkAssetUid, 100)
      }

      checkAssetUid()
    })
  }

  /**
   * Wait for an upload's bytes to have landed, and answer its asset's uid.
   *
   * Unlike `waitForAssetUid`, which answers once the session is open, this resolves only when
   * the upload is `completed` — when the server has the file. Anything that reads the bytes (a
   * transcription, a burn) must wait for this: asked earlier, the server refuses with "its bytes
   * have not landed yet". No timeout, since an upload takes as long as the file is large.
   *
   * **A failure does not end the wait.** A failed upload stays in the queue, where a retry sends
   * it on, and what waited on it then goes ahead as if nothing had happened. It rejects only once
   * the upload has left for good: cancelled, or removed from the queue.
   */
  function waitForCompletion(id: string): Promise<string> {
    return new Promise((resolve, reject) => {
      /** Settles the wait on the upload as it now stands, and says whether it did. */
      const settle = (upload: UploadItem | undefined): boolean => {
        if (!upload || upload.status === 'cancelled') {
          reject(new Error(upload ? 'Upload cancelled' : 'Upload not found'))
          return true
        }
        const assetUid = upload.result?.assetUid ?? upload.assetUid
        if (upload.status !== 'completed' || !assetUid) return false
        resolve(assetUid)
        return true
      }
      if (settle(uploads.value.get(id))) return
      // Detached, so the watch lives as long as the wait rather than as long as the component
      // that happened to be setting up when it began.
      const scope = effectScope(true)
      scope.run(() =>
        watch(
          () => uploads.value.get(id),
          (upload) => {
            if (settle(upload)) scope.stop()
          },
        ),
      )
    })
  }

  function configure(newConfig: Partial<UploadStoreConfig>): void {
    config.value = { ...config.value, ...newConfig }
  }

  function openDrawer(): void {
    drawerOpen.value = true
  }

  /**
   * Ouverture qui suit un dépôt de fichiers, et non un geste réclamé.
   *
   * Séparée d'`openDrawer()` — le bouton Transferts, la reprise d'un import —
   * qui doit toujours aboutir. Celle-ci se tait quand un écran a demandé la
   * place : pendant la quête d'onboarding, le tiroir recouvrait la colonne des
   * étapes. La règle vit ici plutôt que chez les appelants, parce qu'elle leur
   * a déjà échappé trois fois (`useUpload.ts`) — chacun rouvrait le tiroir
   * juste après `addFiles`, par-dessus la garde.
   */
  function openDrawerAutomatically(): void {
    if (!drawerAutoOpenDisabled.value) drawerOpen.value = true
  }

  function closeDrawer(): void {
    drawerOpen.value = false
  }

  function toggleDrawer(): void {
    drawerOpen.value = !drawerOpen.value
  }

  return {
    uploads,
    config,
    drawerOpen,
    drawerTab,
    dropOverlayDisabled,
    drawerAutoOpenDisabled,
    openDrawerAutomatically,
    uploadList,
    pendingUploads,
    activeUploads,
    pausedUploads,
    completedUploads,
    failedUploads,
    hasActiveOrPendingUploads,
    queueSize,
    availableSlots,
    overallProgress,
    uploadSpeed,
    uploadEta,
    addFiles,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    retryUpload,
    retryCreditRefused,
    removeUpload,
    clearCompleted,
    clearFailed,
    clearAll,
    clearInactive,
    cancelActive,
    getUpload,
    waitForAssetUid,
    waitForCompletion,
    configure,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    onUploadComplete,
  }
})
